import {
  FC,
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import {
  NavigationWrapper,
  AppNavigationWrapper,
  User,
  DropDownMenu,
  ArrowWrapper,
  Dropdown,
  DropdownWrapper,
} from "./Navigation.styled";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "auth/authActions";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Arrow from "static/svgs/Arrow";
import LogoWithText from "static/svgs/LogoWithText";
import { GreenButton } from "shared/shared_styled_comps/components.styled";
import { Squash } from "hamburger-react";
import Notifications from "components/Notifications/Notifications";
import { setVideoTour, setSelectedPersona } from "state/appActions";
import { setActiveOrganization } from "api/helpers";
import { Briefcase, BarChart3, Settings as SettingsIcon, Headphones, Heart } from 'lucide-react';


interface NavigationProps {
  setTourStep: Dispatch<SetStateAction<number>>;
  setTourIsOpen: Dispatch<SetStateAction<boolean>>;
}


const solutionsItems = [
  {
    id: "executives",
    icon: Briefcase,
    title: "Executives",
    subtitle: "See metrics execs care about",
  },
  {
    id: "gtm-leaders",
    icon: BarChart3,
    title: "GTM Leaders",
    subtitle: "Learn how GTM leaders prioritize pipeline",
  },
  {
    id: "sales-ops",
    icon: SettingsIcon,
    title: "Sales Ops / Enablement",
    subtitle: "Explore integrations and playbooks for Sales Ops",
  },
  {
    id: "sales-reps",
    icon: Headphones,
    title: "Sales Reps",
    subtitle: "Discover tools that help reps close more deals",
  },
  {
    id: "customer-success",
    icon: Heart,
    title: "Customer Success",
    subtitle: "Build lasting relationships with customers",
  },
];

const Navigation: FC<NavigationProps> = ({ setTourIsOpen, setTourStep }) => {
  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );
  const [isSolutionsDropdownVisible, setIsSolutionsDropdownVisible] =
    useState(false);
  const user = useSelector((state: any) => state.auth.user);
  const [showDropDown, setShowDropDown] = useState(false);
  const [openMobileNav, setOpenMobileNav] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  let dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const userRef = useRef<HTMLDivElement | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { pathname } = location;
  const pageTitle: string = useSelector((state: any) => state.app.pageTitle);

  // Clears authenticated and local storage
  const handleLogout = async () => {
    setShowDropDown(false);
    dispatch(logout());
    navigate("/login");
  };

  const handleImageError = () => {};

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (openMobileNav) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [openMobileNav]);

  useEffect(() => {
    if (openMobileNav) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [openMobileNav]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        menuRef &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        userRef.current &&
        !userRef.current.contains(event.target as Node)
      ) {
        setShowDropDown(false);
      }
    };

    document.addEventListener("click", (event) => handleClickOutside(event));

    return () => {
      document.removeEventListener("click", (event) =>
        handleClickOutside(event)
      );
    };
  }, []);

  const handleGiveFeedback = () => {
    const email = "feedback@leadoff.ai";
    const subject = "Feedback for Leadoff.ai - ";
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}`;
  };

  useEffect(() => {
    setShowDropDown(false);
    setOpenMobileNav(false);
  }, [pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth !== null && window.innerWidth !== undefined) {
        setWindowWidth(window?.innerWidth);
      }
    };

    // Initial width calculation
    handleResize();

    // Event listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isAuthenticated]);

  const handleTourNavigate = (href: string, tourStep: number) => {
    if (!user?.completed_intro_tour && setTourStep && setTourIsOpen) {
      setShowDropDown(false);
      setTourIsOpen(false);
      setTourStep(tourStep);
      navigate(href);
    }
  };

  const isCallRecordingPage = pathname.includes("call-recording");
  const isOrgEqPage = pathname.includes("/org-eq");

  // Hide navigation completely for OrgEQ pages
  if (isOrgEqPage) {
    return <></>;
  }

  // if (!nav_excluded_paths.some((substring) => pathname.includes(substring))) {
  if (!pathname.includes("/app")) {
    return (
      <NavigationWrapper hasScrolled={hasScrolled}>
        <div className="nav-inner-wrapper">
          <div className="logo-wrapper">
            <Link to="/">
              <LogoWithText />
            </Link>
          </div>
          {!isCallRecordingPage && (
            <div
              className={`${
                openMobileNav ? "nav-wrapper open" : "nav-wrapper"
              }`}
            >
              <Link to="/winning-behaviors" style={{ position: "relative" }}>
                <h3>
                  Behavioral Intelligence{" "}
                  <span className="mobile-only flex-span">&#8964;</span>
                </h3>
              </Link>
              <DropdownWrapper
                onMouseEnter={() => setIsSolutionsDropdownVisible(true)}
                onMouseLeave={() => setIsSolutionsDropdownVisible(false)}
              >
                <Link to="" style={{ position: "relative" }}>
                  <h3>
                    Solutions{" "}
                    <span className="mobile-only flex-span">&#8964;</span>
                  </h3>
                </Link>
                <Dropdown 
                  isVisible={isSolutionsDropdownVisible} 
                  className="solutions-dropdown"
                  style={{
                    background: 'white',
                    backdropFilter: 'none',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 6px 10px rgba(0, 0, 0, 0.08)',
                    padding: '8px 0',
                    minWidth: '320px',
                    borderRadius: '12px'
                  }}
                >
                  <div className="solutions-menu" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0'
                  }}>
                    {solutionsItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.id}
                          className="solution-item"
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '12px 16px',
                            textDecoration: 'none',
                            color: '#111827',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                          onClick={() => {
                            dispatch(setSelectedPersona(item.id));
                            navigate('/');
                            setIsSolutionsDropdownVisible(false);
                            // Scroll with more offset after navigation
                            setTimeout(() => {
                              const element = document.getElementById("immersive-persona-experience");
                              if (element) {
                                const yOffset = 20; // Scroll down more (positive offset)
                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: "smooth" });
                              }
                            }, 200);
                          }}
                        >
                          <div
                            className="icon-wrapper"
                            style={{
                              flexShrink: 0,
                              padding: '10px',
                              backgroundColor: 'rgba(0, 191, 99, 0.1)',
                              borderRadius: '8px',
                              marginTop: '2px',
                              transition: 'background-color 0.2s ease'
                            }}
                          >
                            <Icon size={20} color="#00BF63" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: '600',
                              color: '#111827',
                              marginBottom: '2px',
                              fontSize: '14px'
                            }}>
                              {item.title}
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: '#6b7280',
                              lineHeight: '1.4'
                            }}>
                              {item.subtitle}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Dropdown>
              </DropdownWrapper>
              <Link to="/blog">
                <h3>Blog</h3>
              </Link>
              <Link to="/team">
                <h3>Team</h3>
              </Link>
              {/* <Link to="/login">
              <h3>Pricing</h3>
            </Link> */}
              <hr />
              {isAuthenticated ? (
                <>
                  <Link to={`/app/deals`}>
                    <h3>Dashboard</h3>
                  </Link>
                  <Link className="free-trial" to="/app/deals">
                    <GreenButton
                      width={
                        windowWidth && windowWidth < 990
                          ? `fit-content`
                          : "145px"
                      }
                      height={windowWidth && windowWidth < 990 ? 70 : 30}
                      fontSize={windowWidth && windowWidth < 990 ? 40 : 13}
                      paddingright={windowWidth && windowWidth < 990 ? 30 : 0}
                      paddingleft={windowWidth && windowWidth < 990 ? 30 : 0}
                    >
                      My Deals
                    </GreenButton>
                  </Link>
                </>
              ) : (
                <>
                  <Link to={`/login${location.search}`}>
                    <h3>Login</h3>
                  </Link>
                  <Link className="free-trial" to="/contact-us">
                    <GreenButton
                      width={
                        windowWidth && windowWidth < 990
                          ? `fit-content`
                          : "145px"
                      }
                      height={windowWidth && windowWidth < 990 ? 70 : 30}
                      fontSize={windowWidth && windowWidth < 990 ? 40 : 13}
                      paddingright={windowWidth && windowWidth < 990 ? 30 : 0}
                      paddingleft={windowWidth && windowWidth < 990 ? 30 : 0}
                    >
                      Contact Sales
                    </GreenButton>
                  </Link>
                </>
              )}
            </div>
          )}
          <div className="mobile-nav-button">
            <Squash toggled={openMobileNav} toggle={setOpenMobileNav} />
          </div>
        </div>
      </NavigationWrapper>
    );
  } else {
    return (
      <AppNavigationWrapper>
        <div className="title-wrapper">
          <h2 className="title">{pageTitle}</h2>
        </div>
        <div className="right-wrapper">
          {/* {user.intro_completion !== 100 && (
            <CircularProgress
              onClick={() => navigate("/app/get-started")}
              percent={Math.max(
                Math.round(introCompletion),
                user.intro_completion || 0
              )}
            >
              <div className="circle-progress">
                <div className="circle-progress-left" />
                <div className="circle-progress-right" />
              </div>
              <span>
                {Math.max(
                  Math.round(introCompletion),
                  user.intro_completion || 0
                )}
                %
              </span>
            </CircularProgress>
          )} */}
          {/* <button onClick={() => navigate(-1)}>{"<-"}</button> */}
          <User
            className="user-settings"
            onClick={() => setShowDropDown(true)}
            ref={userRef}
          >
            <Notifications />
            <div className="user-wrapper">
              <div className="pfp">
                {user.image_url ? (
                  <img src={user.image_url} onError={handleImageError} />
                ) : (
                  user.first_name && (
                    <div className="letter-wrapper">
                      {user.first_name[0].toUpperCase()}
                    </div>
                  )
                )}
              </div>
              <div className="text">
                <p className="sub-text">Welcome back!</p>
                <p>
                  {user.first_name}{" "}
                  {user && user.last_name
                    ? user.last_name[0].toUpperCase()
                    : ""}
                  .
                </p>
              </div>
              <ArrowWrapper showdropdown={showDropDown}>
                <Arrow />
              </ArrowWrapper>
            </div>
          </User>
        </div>
        {showDropDown && (
          <DropDownMenu ref={menuRef}>
            <h4>
              {user.first_name} {user.last_name}
            </h4>
            <p className="email">{user.email}</p>
            <hr className="first" />
            {user.organizations_info && (
              <>
                <h4 style={{ marginBottom: "5px" }}>Accounts</h4>
                {user.organizations_info.map((org: any) => (
                  <div
                    key={org.organization_name}
                    className={`org-item ${
                      org._id === user.organization_info._id ? "active" : ""
                    }`}
                    onClick={async () => {
                      if (org._id !== user.organization_info._id) {
                        await setActiveOrganization(org._id);
                        window.location.reload();
                      }
                    }}
                  >
                    <div
                      className="org-circle"
                      style={{
                        backgroundColor:
                          org._id === user.organization_info._id
                            ? "#4caf50"
                            : "transparent",
                        color:
                          org._id === user.organization_info._id
                            ? "white"
                            : "inherit",
                      }}
                    >
                      {org.organization_name[0].toUpperCase()}
                    </div>
                    <div className="org-details">
                      <p className="org-name">
                        {org.organization_name}
                        {org.main && (
                          <span style={{ marginLeft: "4px" }}>★</span>
                        )}
                      </p>
                      {org._id === user.organization_info._id ? (
                        <span className="active-label">Active</span>
                      ) : (
                        org.main && <span className="active-label">Main</span>
                      )}
                    </div>
                  </div>
                ))}
                <hr className="first" />
              </>
            )}
            <h4>Settings</h4>

            <Link to="/app/settings/profile">
              <p className="link">My Profile</p>
            </Link>

            {user && !user?.completed_intro_tour ? (
              <a
                className="tour-link"
                onClick={() =>
                  handleTourNavigate("/app/settings/company-account", 20)
                }
              >
                Company Account
              </a>
            ) : (
              <Link to="/app/settings/company-account">
                <p className="link">Company Account</p>
              </Link>
            )}
            {user && !user?.completed_intro_tour ? (
              <a
                className="tour-link"
                onClick={() =>
                  handleTourNavigate("/app/settings/integrations", 18)
                }
              >
                Integrations
              </a>
            ) : (
              <Link to="/app/settings/integrations">
                <p className="link">Integrations</p>
              </Link>
            )}
            <p
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShowDropDown(false);
                dispatch(setVideoTour());
              }}
              className="link"
            >
              Tutorial Video
            </p>
            {/* <Link to="/app/settings/billing">
              <p className="link">Billing</p>
            </Link> */}
            {/* <Link to="/app/settings/reports">
              <p className="link">My Reports</p>
            </Link> */}
            {/* <Link to="/app/settings/notifications">
              <p className="link">Notifications</p>
            </Link> */}

            {/* <Link to="/app/settings/coaching">
              <p className="link">Coaching Materials</p>
            </Link> */}
            {/* <Link to="/app/settings/integrations">
              <p className="link">Integrations</p>
            </Link> */}
            <hr />
            <Link to="/app/settings/company-account?invite=true">
              <button onClick={() => setShowDropDown(false)}>
                Refer Teammates
              </button>
            </Link>
            <button onClick={handleGiveFeedback}>Give Feedback</button>
            <button onClick={handleLogout}>Logout</button>
          </DropDownMenu>
        )}
      </AppNavigationWrapper>
    );
  }
  // }

  return <></>;
};

export default Navigation;
